import React, { useCallback, useRef } from 'react'
import {
  Image,
  Box,
  CustomLink,
  LinkAsType,
  ImageProps,
  Link,
  Pill,
  HeartToggle,
  Flex,
} from '@moonpig/launchpad-components'
import { styled, breakpointUp, css } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql'
import { useLocaleText } from '../ProductTile.locale'
import { HeartIcon } from '../HeartIcon'
import { PillVariant, ProductImage } from '../../../types'
import { SponsoredPill } from '../../SponsoredPill'
import {
  getProductTileCtaLabel,
  getProductTileCtaType,
  ProductTileCtaEnum,
} from '../getProductTileCta'
import { getGiftTileCtaIcon } from './getGiftTileCtaIcon'
import { useHover } from '../NonCardProductTileWithCarousel/useHover'
import { ImageCarousel } from '../NonCardProductTileWithCarousel/ImageCarousel/ImageCarousel'
import { colorValue } from '@moonpig/launchpad-theme'

const TITLE_HEIGHT = '48px'

const StyledImage = styled(Image)<{ $showBorder?: boolean }>`
  ${({ $showBorder }) =>
    s({
      borderRadius: $showBorder ? 3 : 2,
      border: '1px solid',
      borderColor: 'colorBlack10',
    })};
`

const StyledCarouselWrapper = styled.div<{ $showBorder?: boolean }>`
  ${({ $showBorder }) =>
    s({
      borderRadius: $showBorder ? 3 : 2,
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'colorBlack10',
    })};
`

const StyledAnchorOverlay = styled(Link)`
  position: absolute;
  width: 100%;
  height: 100%;
`

const StyledTile = styled.div<{ $showBorder?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${({ $showBorder }) =>
    s({
      bgcolor: 'colorBackground01',
      borderRadius: $showBorder ? 4 : 2,
    })};

  ${({ $showBorder }) =>
    $showBorder &&
    s({
      boxShadow: 2,
      pt: 3,
      px: 3,
      pb: 5,
    })};

  &:hover {
    cursor: pointer;
    ${({ $showBorder }) => $showBorder && s({ boxShadow: 3 })};
  }
`

const StyledInformationContainer = styled.div<{ $showBorder?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    ${({ $showBorder }) =>
      $showBorder &&
      s({
        px: 4,
      })}
`

const StyledPriceDetailsContainer = styled(Flex)`
  order: 2;
  flex-flow: row;
  align-items: center;
  justify-content: space-between;
  row-gap: 4px;
  min-height: 44px;
  margin-top: auto;
`

const StyledPriceContainer = styled(Flex)<{ $compact?: boolean }>`
  flex-direction: column;
  align-items: flex-start;
    
  ${({ $compact }) =>
    !$compact &&
    css`
      ${breakpointUp('md')} {
        flex-direction: row;
        align-items: baseline;
        column-gap: 8px;
      }
    `}

  & > * {
    margin-bottom: 0;
  }
`
const StyledPriceFrom = styled.span`
  ${s({
    typography: 'typeBodyText',
    color: 'colorTextLabel',
    fontSize: 16,
  })};
`
const StyledPriceAmount = styled.span`
  ${s({
    typography: {
      xs: 'typeMobileDisplay05',
      md: 'typeDesktopDisplay06',
    },
    color: 'colorTextLabel',
  })};
`

const StyledPrice = styled.p<{ $compact?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;

    ${({ $compact }) =>
      !$compact &&
      css`
        ${breakpointUp('md')} {
          flex-direction: row;
          align-items: baseline;
          gap: 4px;
        }
    `}
`

const StyledFullPrice = styled.p`
  ${s({
    color: 'colorBlack70',
    typography: 'display05',
  })};
`

const StyledCallToAction = styled(CustomLink)`
  ${s({
    typography: 'typeBodyLabel',
    p: 0,
    m: 0,
    bgcolor: 'colorInteractionButton',
    borderRadius: 40,
    width: 44,
    height: 44,
    minHeight: 44,
    border: 0,
    boxShadow: 0,
  })}
  position: relative;
  z-index: 1;
  order: 3;
  &:hover:not(.loading) {
    ${s({
      bgcolor: 'colorInteractionButtonHover',
    })}
  }
  &.loading {
    ${s({
      bgcolor: 'transparent',
    })}
  }
`

const StyledOurPickWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  width: 100%;
  transform: translateY(-50%);
  z-index: 1;
`

const StyledHeartToggleWrapper = styled.div`
  ${s({
    position: 'absolute',
    zIndex: 2,
    top: 4,
    right: 4,
  })}

  & > button:not(:disabled):focus {
    outline: transparent;
    ${s({ boxShadow: 0 })}
  }
  & > button:not(:disabled):focus-visible > span:first-child {
    box-shadow: inset 0 0 0 2px ${colorValue('colorPrimary02')};
  }
`
const StyledMarketingPill = styled(Pill)`
font-weight: 300;
  ${s({
    bgcolor: 'colorBackgroundHero',
    color: 'colorTextLabel',
    padding: { xs: '2px 6px', md: '2px 8px' },
    fontSize: { xs: '12px', md: '14px' },
    lineHeight: { xs: '16px', md: '18px' },
  })}
`

const StyledPill = styled(Pill)`
font-weight: 300;
  ${s({
    padding: { xs: '2px 6px', md: '2px 8px' },
    fontSize: { xs: '12px', md: '14px' },
    lineHeight: { xs: '16px', md: '18px' },
  })}
`

const StyledTitle = styled.p`
  ${s({
    typography: 'typeBodyText',
    color: 'colorTextLabel',
    marginTop: 4,
    marginBottom: 0,
    order: 1,
  })};
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  height: ${TITLE_HEIGHT};

  ${StyledTile}:hover:not(:has(${StyledCallToAction}:hover, ${StyledHeartToggleWrapper}:hover)) & {
    text-decoration: underline;
  }

  @supports (-webkit-line-clamp: 2) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyledCompAs = keyof React.JSX.IntrinsicElements | React.ComponentType<any>

type Labels = {
  moreInformation?: string
  addToBasket?: string
}

type ProductPill = {
  displayLabel: string
  displayVariant: PillVariant
}

type GiftTileVariantProps = {
  href: string
  title: string
  price: string
  fullPrice?: string | null
  discountedPercentage?: number | null
  image: {
    src: ImageProps['src']
    alt: string
  }
  images?: ProductImage[]
  productId: string
  priceAs?: StyledCompAs
  fullPriceAs?: StyledCompAs
  titleAs?: StyledCompAs
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  /** Determines the cta button action. If it is not passed, the cta button will not be rendered */
  linkAs?: LinkAsType
  ref?: React.Ref<HTMLDivElement>
  /** Determines the favourite toggle state props */
  favourite?: {
    /** State of the favourite toggle */
    isSelected?: boolean
    /** Callback function to change the state of the favourite toggle */
    onSelect: (e: React.MouseEvent<HTMLButtonElement>) => void
  }
  labels?: Labels
  ourPick?: boolean
  sponsored?: boolean
  pill?: ProductPill
  department?: DepartmentsEnum
  isPrintedInCard?: boolean
  hasMultipleVariants?: boolean
  isCustomisable?: boolean
  showBorder?: boolean
  compact?: boolean
}

export const GiftTileVariant = React.forwardRef(
  (props: GiftTileVariantProps, ref?: React.Ref<HTMLAnchorElement>) => {
    const {
      price,
      fullPrice,
      discountedPercentage,
      title,
      image,
      images = [],
      href,
      priceAs,
      fullPriceAs,
      titleAs,
      onClick,
      linkAs,
      favourite,
      labels,
      ourPick,
      pill,
      sponsored,
      productId,
      department,
      isPrintedInCard = false,
      hasMultipleVariants = false,
      isCustomisable = false,
      showBorder = false,
      compact = false,
      ...rest
    } = props

    const localiseText = useLocaleText()
    const [hoverRef, isHovered] = useHover()
    const anchorRef = useRef<HTMLAnchorElement>(null)

    const moreInformationText =
      labels?.moreInformation ??
      localiseText('non_card_product_tile.more_information')

    const addToBasketText =
      labels?.addToBasket ?? localiseText('common.add_to_basket_cta')

    const showDiscountDetails = fullPrice && discountedPercentage

    const fromPriceLabel = localiseText('non_card_product_tile.from_price')
    const priceAmount = hasMultipleVariants
      ? price.slice(fromPriceLabel.length + 1)
      : price

    const ctaParams = department
      ? {
          department,
          hasMultipleVariants,
          isCustomisable,
          isPrintedInCard,
        }
      : null
    const ctaType = ctaParams
      ? getProductTileCtaType(ctaParams)
      : ProductTileCtaEnum.AddToBasket
    const ctaLabel = ctaParams
      ? getProductTileCtaLabel(ctaParams, localiseText)
      : addToBasketText
    const sanitisedTitle = title.toLowerCase().replace(/\s/g, '-')

    const priceContainerId = `price-container-${sanitisedTitle}`
    const priceId = `price-${sanitisedTitle}`
    const fullPriceId = `full-price-${sanitisedTitle}`

    const explainPrice = () => {
      if (!fullPrice || !discountedPercentage) {
        return localiseText('common.price_explanation', price)
      }

      return localiseText(
        'common.price_with_discount_explanation',
        price,
        fullPrice,
        discountedPercentage,
      )
    }

    const imageTapHandler = () => {
      anchorRef.current?.click()
    }
    const setAnchorRef = useCallback(
      (el: HTMLAnchorElement | null) => {
        ;(
          anchorRef as React.MutableRefObject<HTMLAnchorElement | null>
        ).current = el
        const forwardedRef = ref
        /* istanbul ignore next */
        if (typeof forwardedRef === 'function') {
          forwardedRef(el)
        } else if (forwardedRef) {
          ;(
            forwardedRef as React.MutableRefObject<HTMLAnchorElement | null>
          ).current = el
        }
      },
      [ref],
    )

    return (
      <StyledTile
        aria-label={`${title} ${price}`}
        data-testid="shared-products-gift-tile"
        $showBorder={showBorder}
        ref={hoverRef}
        {...rest}
      >
        <StyledAnchorOverlay
          href={href}
          ref={setAnchorRef}
          title={onClick ? moreInformationText : addToBasketText}
          onClick={onClick}
        />
        {favourite && (
          <StyledHeartToggleWrapper>
            <HeartToggle
              ariaLabel={`${
                favourite.isSelected
                  ? localiseText(
                      'non_card_product_tile.remove_from_favourites',
                      title,
                    )
                  : localiseText(
                      'non_card_product_tile.add_to_favourites',
                      title,
                    )
              }`}
              isToggled={!!favourite.isSelected}
              onToggle={(e: React.MouseEvent<HTMLButtonElement>) =>
                favourite.onSelect(e)
              }
            />
          </StyledHeartToggleWrapper>
        )}
        {images.length > 1 ? (
          <StyledCarouselWrapper $showBorder={showBorder}>
            <ImageCarousel
              title={title}
              isHovered={isHovered}
              productImages={images}
              onTapToCTA={imageTapHandler}
            />
          </StyledCarouselWrapper>
        ) : (
          <StyledImage
            {...image}
            $showBorder={showBorder}
            aspectRatio={1}
            onClick={imageTapHandler}
          />
        )}
        <StyledInformationContainer $showBorder={showBorder}>
          <StyledTitle aria-describedby={`${priceId}`} as={titleAs}>
            {title}
          </StyledTitle>
          <Box display="flex" flexWrap="wrap" mt={4} order={1}>
            {sponsored && (
              <Box zIndex={1}>
                <SponsoredPill />
              </Box>
            )}
            {!sponsored &&
              pill &&
              (pill.displayVariant === 'marketing' ? (
                <StyledMarketingPill
                  label={pill.displayLabel}
                  variant="marketing"
                />
              ) : (
                <StyledPill
                  label={pill.displayLabel}
                  variant={pill.displayVariant}
                />
              ))}
          </Box>
          <StyledPriceDetailsContainer
            aria-label={explainPrice()}
            data-testid={priceContainerId}
          >
            <StyledPriceContainer $compact={compact}>
              <StyledPrice
                as={priceAs}
                data-testid={priceId}
                id={priceId}
                $compact={compact}
              >
                {hasMultipleVariants ? (
                  <>
                    <StyledPriceFrom>{fromPriceLabel}</StyledPriceFrom>
                    <StyledPriceAmount>{priceAmount}</StyledPriceAmount>
                  </>
                ) : (
                  <StyledPriceAmount>{price}</StyledPriceAmount>
                )}
              </StyledPrice>
              {showDiscountDetails && (
                <StyledFullPrice
                  as={fullPriceAs}
                  data-testid={fullPriceId}
                  style={{
                    textDecoration: 'line-through',
                  }}
                >
                  {fullPrice}
                </StyledFullPrice>
              )}
            </StyledPriceContainer>
            {linkAs && (
              <StyledCallToAction
                aria-hidden={!onClick}
                aria-label={ctaLabel}
                tabIndex={!onClick ? -1 : undefined}
                className="add-to-basket-cta"
                data-product-id={productId}
                linkAs={linkAs}
                to={href}
              >
                {getGiftTileCtaIcon(ctaType)}
              </StyledCallToAction>
            )}
          </StyledPriceDetailsContainer>
        </StyledInformationContainer>
        {ourPick && (
          <StyledOurPickWrapper>
            <Pill icon={<HeartIcon />} label={localiseText('our_pick')} />
          </StyledOurPickWrapper>
        )}
      </StyledTile>
    )
  },
)
